import React, { useState } from 'react';
import { WrenchIcon, CalendarIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, PlusIcon, TruckIcon } from 'lucide-react';
interface Truck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  status: string;
  lastMaintenance: string;
  nextMaintenance: string;
  mileage: number;
  currentHours: number;
  hoursSinceLastMaintenance: number;
  maintenanceIntervalHours: number;
}
interface MaintenanceRecord {
  id: string;
  truckId: string;
  type: 'routine' | 'repair' | 'inspection' | 'emergency';
  description: string;
  scheduledDate: string;
  completedDate?: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'overdue';
  cost?: number;
  notes?: string;
}
interface MaintenanceScheduleProps {
  trucks: Truck[];
}
export const MaintenanceSchedule = ({
  trucks
}: MaintenanceScheduleProps) => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState<string>('');
  // Sample maintenance records
  const [maintenanceRecords] = useState<MaintenanceRecord[]>([{
    id: '1',
    truckId: '1',
    type: 'routine',
    description: 'Oil change and filter replacement',
    scheduledDate: '2024-04-01',
    status: 'scheduled',
    cost: 150
  }, {
    id: '2',
    truckId: '2',
    type: 'inspection',
    description: 'DOT annual inspection',
    scheduledDate: '2024-03-15',
    status: 'scheduled',
    cost: 200
  }, {
    id: '3',
    truckId: '3',
    type: 'repair',
    description: 'Brake pad replacement',
    scheduledDate: '2024-01-20',
    status: 'in-progress',
    cost: 450
  }, {
    id: '4',
    truckId: '1',
    type: 'routine',
    description: 'Tire rotation and inspection',
    scheduledDate: '2024-01-05',
    completedDate: '2024-01-05',
    status: 'completed',
    cost: 75,
    notes: 'All tires in good condition'
  }]);
  const getMaintenanceTypeColor = (type: string) => {
    switch (type) {
      case 'routine':
        return 'bg-blue-100 text-blue-800';
      case 'repair':
        return 'bg-red-100 text-red-800';
      case 'inspection':
        return 'bg-purple-100 text-purple-800';
      case 'emergency':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled':
        return <CalendarIcon size={16} className="text-yellow-500" data-id="element-3857" />;
      case 'in-progress':
        return <ClockIcon size={16} className="text-blue-500" data-id="element-3858" />;
      case 'completed':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-3859" />;
      case 'overdue':
        return <AlertTriangleIcon size={16} className="text-red-500" data-id="element-3860" />;
      default:
        return <WrenchIcon size={16} className="text-gray-500" data-id="element-3861" />;
    }
  };
  const getTruckInfo = (truckId: string) => {
    const truck = trucks.find(t => t.id === truckId);
    return truck ? `${truck.truckNumber} (${truck.make} ${truck.model})` : 'Unknown Truck';
  };
  const isMaintenanceDue = (truck: Truck) => {
    const hoursRemaining = truck.maintenanceIntervalHours - truck.hoursSinceLastMaintenance;
    return hoursRemaining <= 50 && hoursRemaining > 0; // Due soon if within 50 hours
  };
  const isMaintenanceOverdue = (truck: Truck) => {
    return truck.hoursSinceLastMaintenance >= truck.maintenanceIntervalHours;
  };
  const upcomingMaintenance = trucks.filter(isMaintenanceDue);
  const expiredTrucks = trucks.filter(isMaintenanceOverdue);
  const activeRecords = maintenanceRecords.filter(r => r.status !== 'completed');
  const recentCompleted = maintenanceRecords.filter(r => r.status === 'completed').slice(0, 3);
  return <div className="space-y-6" data-id="element-3862">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3863">
        <div className="bg-white p-4 rounded-lg shadow" data-id="element-3864">
          <div className="flex items-center justify-between" data-id="element-3865">
            <div data-id="element-3866">
              <p className="text-sm font-medium text-gray-600" data-id="element-3867">Due Soon</p>
              <p className="text-2xl font-bold text-orange-600" data-id="element-3868">
                {upcomingMaintenance.length}
              </p>
            </div>
            <AlertTriangleIcon size={32} className="text-orange-500" data-id="element-3869" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow" data-id="element-3870">
          <div className="flex items-center justify-between" data-id="element-3871">
            <div data-id="element-3872">
              <p className="text-sm font-medium text-gray-600" data-id="element-3873">Expired</p>
              <p className="text-2xl font-bold text-red-600" data-id="element-3874">
                {expiredTrucks.length}
              </p>
            </div>
            <AlertTriangleIcon size={32} className="text-red-500" data-id="element-3875" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow" data-id="element-3876">
          <div className="flex items-center justify-between" data-id="element-3877">
            <div data-id="element-3878">
              <p className="text-sm font-medium text-gray-600" data-id="element-3879">In Progress</p>
              <p className="text-2xl font-bold text-blue-600" data-id="element-3880">
                {maintenanceRecords.filter(r => r.status === 'in-progress').length}
              </p>
            </div>
            <ClockIcon size={32} className="text-blue-500" data-id="element-3881" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow" data-id="element-3882">
          <div className="flex items-center justify-between" data-id="element-3883">
            <div data-id="element-3884">
              <p className="text-sm font-medium text-gray-600" data-id="element-3885">This Month</p>
              <p className="text-2xl font-bold text-green-600" data-id="element-3886">
                {maintenanceRecords.filter(r => {
                const date = new Date(r.scheduledDate);
                const now = new Date();
                return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
              }).length}
              </p>
            </div>
            <CalendarIcon size={32} className="text-green-500" data-id="element-3887" />
          </div>
        </div>
      </div>

      {/* Expired Maintenance Alert */}
      {expiredTrucks.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-3888">
          <div className="flex items-center gap-2 mb-3" data-id="element-3889">
            <AlertTriangleIcon size={20} className="text-red-500" data-id="element-3890" />
            <h3 className="font-semibold text-red-800" data-id="element-3891">
              Overdue Maintenance - Immediate Action Required
            </h3>
          </div>
          <div className="space-y-2" data-id="element-3892">
            {expiredTrucks.map(truck => <div key={truck.id} className="flex items-center justify-between bg-white rounded-md p-3" data-id="element-3893">
                <div className="flex items-center gap-3" data-id="element-3894">
                  <TruckIcon size={16} className="text-red-400" data-id="element-3895" />
                  <span className="font-medium" data-id="element-3896">{truck.truckNumber}</span>
                  <span className="text-sm text-gray-500" data-id="element-3897">
                    {truck.make} {truck.model}
                  </span>
                </div>
                <div className="text-sm text-red-600 font-medium" data-id="element-3898">
                  Overdue by:{' '}
                  {truck.hoursSinceLastMaintenance - truck.maintenanceIntervalHours}{' '}
                  hours
                </div>
              </div>)}
          </div>
        </div>}

      {/* Upcoming Maintenance Alert */}
      {upcomingMaintenance.length > 0 && <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-id="element-3899">
          <div className="flex items-center gap-2 mb-3" data-id="element-3900">
            <AlertTriangleIcon size={20} className="text-orange-500" data-id="element-3901" />
            <h3 className="font-semibold text-orange-800" data-id="element-3902">
              Maintenance Due Soon
            </h3>
          </div>
          <div className="space-y-2" data-id="element-3903">
            {upcomingMaintenance.map(truck => <div key={truck.id} className="flex items-center justify-between bg-white rounded-md p-3" data-id="element-3904">
                <div className="flex items-center gap-3" data-id="element-3905">
                  <TruckIcon size={16} className="text-gray-400" data-id="element-3906" />
                  <span className="font-medium" data-id="element-3907">{truck.truckNumber}</span>
                  <span className="text-sm text-gray-500" data-id="element-3908">
                    {truck.make} {truck.model}
                  </span>
                </div>
                <div className="text-sm text-orange-600" data-id="element-3909">
                  Due in:{' '}
                  {truck.maintenanceIntervalHours - truck.hoursSinceLastMaintenance}{' '}
                  hours
                </div>
              </div>)}
          </div>
        </div>}

      {/* Active Maintenance */}
      <div className="bg-white rounded-lg shadow p-6" data-id="element-3910">
        <div className="flex justify-between items-center mb-4" data-id="element-3911">
          <h3 className="text-lg font-semibold" data-id="element-3912">Active Maintenance</h3>
          <button onClick={() => setShowScheduleModal(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-3913">
            <PlusIcon size={18} data-id="element-3914" />
            Schedule Maintenance
          </button>
        </div>
        {activeRecords.length === 0 ? <div className="text-center py-8" data-id="element-3915">
            <WrenchIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-3916" />
            <p className="text-gray-600" data-id="element-3917">No active maintenance scheduled</p>
          </div> : <div className="space-y-3" data-id="element-3918">
            {activeRecords.map(record => <div key={record.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-3919">
                <div className="flex items-center justify-between mb-2" data-id="element-3920">
                  <div className="flex items-center gap-3" data-id="element-3921">
                    {getStatusIcon(record.status)}
                    <div data-id="element-3922">
                      <h4 className="font-medium" data-id="element-3923">{record.description}</h4>
                      <p className="text-sm text-gray-500" data-id="element-3924">
                        {getTruckInfo(record.truckId)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3925">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMaintenanceTypeColor(record.type)}`} data-id="element-3926">
                      {record.type.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`} data-id="element-3927">
                      {record.status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600" data-id="element-3928">
                  <span data-id="element-3929">
                    Scheduled:{' '}
                    {new Date(record.scheduledDate).toLocaleDateString()}
                  </span>
                  {record.cost && <span data-id="element-3930">Est. Cost: ${record.cost}</span>}
                </div>
              </div>)}
          </div>}
      </div>

      {/* Recent Completed */}
      {recentCompleted.length > 0 && <div className="bg-white rounded-lg shadow p-6" data-id="element-3931">
          <h3 className="text-lg font-semibold mb-4" data-id="element-3932">Recently Completed</h3>
          <div className="space-y-3" data-id="element-3933">
            {recentCompleted.map(record => <div key={record.id} className="border rounded-lg p-4" data-id="element-3934">
                <div className="flex items-center justify-between mb-2" data-id="element-3935">
                  <div className="flex items-center gap-3" data-id="element-3936">
                    <CheckCircleIcon size={16} className="text-green-500" data-id="element-3937" />
                    <div data-id="element-3938">
                      <h4 className="font-medium" data-id="element-3939">{record.description}</h4>
                      <p className="text-sm text-gray-500" data-id="element-3940">
                        {getTruckInfo(record.truckId)}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMaintenanceTypeColor(record.type)}`} data-id="element-3941">
                    {record.type.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600" data-id="element-3942">
                  <span data-id="element-3943">
                    Completed:{' '}
                    {record.completedDate && new Date(record.completedDate).toLocaleDateString()}
                  </span>
                  {record.cost && <span data-id="element-3944">Cost: ${record.cost}</span>}
                </div>
                {record.notes && <p className="text-sm text-gray-600 mt-2 italic" data-id="element-3945">
                    {record.notes}
                  </p>}
              </div>)}
          </div>
        </div>}

      {/* Schedule Maintenance Modal */}
      {showScheduleModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-3946">
          <div className="bg-white rounded-lg p-6 w-full max-w-md" data-id="element-3947">
            <h3 className="text-lg font-semibold mb-4" data-id="element-3948">Schedule Maintenance</h3>
            <p className="text-gray-600 mb-4" data-id="element-3949">
              Schedule maintenance form would be implemented here
            </p>
            <div className="flex justify-end gap-3" data-id="element-3950">
              <button onClick={() => setShowScheduleModal(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-3951">
                Cancel
              </button>
              <button className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700" data-id="element-3952">
                Schedule
              </button>
            </div>
          </div>
        </div>}
    </div>;
};